import { useTranslation } from 'react-i18next'
import type { DataFoundation } from '@/lib/api/amazon-listing.types'

interface SlotStrategyFooterProps {
  contentStrategy: string | null
  dataFoundation: DataFoundation | null
}

export function SlotStrategyFooter({ contentStrategy, dataFoundation }: SlotStrategyFooterProps) {
  const { t } = useTranslation()
  if (!contentStrategy && !dataFoundation) return null

  return (
    <div className="border-t border-neutral-200 dark:border-neutral-700 bg-neutral-50/50 dark:bg-neutral-800/30 px-6 py-4">
      <div className={`grid gap-6 ${contentStrategy && dataFoundation ? 'grid-cols-2' : 'grid-cols-1'}`}>
        {contentStrategy && (
          <div>
            <div className="text-2xs uppercase tracking-wide text-orange-500 font-semibold mb-2">
              {t('amazonListing.concept.conversionStrategy')}
            </div>
            <p className="text-xs text-neutral-600 dark:text-neutral-300 leading-relaxed">
              {contentStrategy}
            </p>
          </div>
        )}
        {dataFoundation && (
          <div>
            <div className="text-2xs uppercase tracking-wide text-amber-500 font-semibold mb-2">
              {t('amazonListing.concept.dataFoundation')}
              <span className="ml-2 bg-amber-500/10 text-amber-500 px-1.5 py-0.5 rounded-full text-3xs">
                {dataFoundation.total_points} {t('amazonListing.concept.dataPoints')}
              </span>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <FoundationList
                label={t('amazonListing.concept.conversionDrivers')}
                items={dataFoundation.conversion_drivers}
                field="feature"
                color="text-orange-500"
              />
              <FoundationList
                label={t('amazonListing.concept.reviewInsights')}
                items={dataFoundation.review_insights}
                field="aspect"
                color="text-amber-500"
              />
              <FoundationList
                label={t('amazonListing.concept.productFeatures')}
                items={dataFoundation.product_features}
                field="feature"
                color="text-emerald-500"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function FoundationList({ label, items, field, color }: {
  label: string
  items: Array<Record<string, unknown>>
  field: string
  color: string
}) {
  if (!items.length) return null
  return (
    <div>
      <div className="text-3xs uppercase text-neutral-500 mb-1">{label}</div>
      {items.map((item, i) => (
        <div
          key={i}
          className="bg-white dark:bg-neutral-800 rounded-md px-2 py-1 text-2xs flex gap-1.5 items-baseline mb-1"
        >
          <span className={`${color} font-bold`}>#{item.index as number}</span>
          <span className="text-neutral-600 dark:text-neutral-300">
            {item[field] as string}
          </span>
        </div>
      ))}
    </div>
  )
}
